import { Component, OnInit } from '@angular/core';
import { FinanceiroService, AssinaturaDto } from './financeiro.service';
import { NotificationService } from '../../core/notification.service';
import { Router } from '@angular/router';

@Component({
  standalone: false,
  selector: 'app-assinaturas-list',
  templateUrl: './assinaturas-list.component.html',
  styleUrls: ['./assinaturas-list.component.css']
})
export class AssinaturasListComponent implements OnInit {
  loading = false;
  page = 1;
  pageSize = 20;
  total = 0;

  status = '';
  dias = 30;
  items: AssinaturaDto[] = [];
  search = '';
  planFilter = '';

  filteredItems: AssinaturaDto[] = [];

  statusOptions = [
    { value: '', label: 'Todas' },
    { value: 'ativa', label: 'Ativas' },
    { value: 'vencida', label: 'Vencidas' },
    { value: 'vencendo', label: 'Vencendo' }
  ];

  planOptions = [
    { value: '', label: 'Todos os planos' },
    { value: 'free', label: '📦 Free' },
    { value: 'vitrine', label: '⭐ Vitrine' }
  ];

  constructor(private svc: FinanceiroService, private notify: NotificationService, private router: Router) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.svc.listarAssinaturas({ status: this.status || undefined, dias: this.status === 'vencendo' ? this.dias : undefined, page: this.page, pageSize: this.pageSize }).subscribe({
      next: (res: any) => {
        const rawItems = this.extractAssinaturas(res);
        this.items = rawItems.map((item: any) => this.normalizeAssinatura(item));
        this.applyFilters();
        this.total = this.extractTotal(res, this.items.length);
        this.loading = false;
      },
      error: (err) => {
        console.error('[Financeiro] erro ao carregar assinaturas', err);
        this.notify.showError('Erro ao carregar assinaturas');
        this.loading = false;
      }
    });
  }

  onPageChange(p: number) {
    this.page = p;
    this.load();
  }

  onFilterChange() {
    this.page = 1;
    this.applyFilters();
  }

  clearFilters() {
    this.search = '';
    this.planFilter = '';
    this.status = '';
    this.page = 1;
    this.load();
  }

  private applyFilters() {
    const term = this.search.trim().toLowerCase();
    this.filteredItems = this.items.filter(item => {
      const matchSearch = !term
        || item.fornecedor?.nome?.toLowerCase().includes(term)
        || item.fornecedor?.id?.toLowerCase().includes(term)
        || item.id?.toLowerCase().includes(term);

      const normalizedPlan = String(item.planLevel || '').toLowerCase();
      const matchPlan = !this.planFilter || normalizedPlan.includes(this.planFilter);

      return matchSearch && matchPlan;
    });
  }

  private normalizeAssinatura(item: any): AssinaturaDto {
    return {
      id: String(item.id || item.Id || item.assinaturaId || item.subscriptionId || ''),
      fornecedor: {
        id: String(item.fornecedor?.id || item.fornecedorId || item.supplierId || item.supplier?.id || ''),
        nome: item.fornecedor?.nome || item.fornecedorNome || item.supplierName || item.supplier?.name || '—'
      },
      planLevel: item.planLevel || item.planLevelText || item.plano || 'free',
      vitrineSinceDate: item.vitrineSinceDate || item.vitrineSince,
      proximoVencimento: item.proximoVencimento || item.nextDueDate || item.subscription?.nextDueDate,
      subscriptionExpiresAt: item.subscriptionExpiresAt || item.expiresAt || item.subscription?.expiresAt,
      diasParaVencimento: item.diasParaVencimento ?? item.daysToDue,
      status: item.status || item.paymentStatus || item.subscription?.status || '—',
      paymentStatus: item.paymentStatus || item.subscription?.status,
      paymentMethod: item.paymentMethod || item.subscription?.paymentMethod,
      autoRenew: item.autoRenew ?? item.subscription?.autoRenew,
      ativo: item.ativo,
      isClaimed: item.isClaimed,
      leadCount: item.leadCount ?? item.leads,
      valor: item.valor,
      valorMensal: item.valorMensal ?? item.monthlyValue
    };
  }

  private extractAssinaturas(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.items)) return res.items;
    if (Array.isArray(res?.results)) return res.results;
    if (Array.isArray(res?.data?.items)) return res.data.items;
    if (Array.isArray(res?.data?.results)) return res.data.results;
    return [];
  }

  private extractTotal(res: any, fallback: number): number {
    return res?.meta?.total
      || res?.total
      || res?.count
      || res?.data?.meta?.total
      || res?.data?.total
      || fallback;
  }

  get dueSoonCount(): number {
    return this.items.filter(i => (i.diasParaVencimento ?? 9999) <= 7 && (i.diasParaVencimento ?? 9999) >= 0).length;
  }

  get overdueCount(): number {
    return this.items.filter(i => (i.diasParaVencimento ?? 1) < 0 || String(i.status).toLowerCase().includes('venc')).length;
  }

  get activeCount(): number {
    return this.items.filter(i => this.normalizeStatus(i) === 'Ativa').length;
  }

  getPaymentMethodLabel(method?: string): string {
    if (!method) return '—';
    const m = String(method).toLowerCase();
    if (m.includes('credit')) return '💳 Cartão';
    if (m.includes('boleto')) return '📄 Boleto';
    if (m.includes('pix')) return '📱 PIX';
    return method;
  }

  getPlanLabel(plan: string): string {
    const p = String(plan || '').toLowerCase();
    return p.includes('vitrine') || p === '1' ? '⭐ Vitrine' : '📦 Free';
  }

  normalizeStatus(item: AssinaturaDto): 'Ativa' | 'Vencendo' | 'Vencida' | 'Pendente' {
    const status = String(item.paymentStatus || item.status || '').toLowerCase();
    const dias = item.diasParaVencimento;

    if (status.includes('pending')) return 'Pendente';
    if (status.includes('overdue') || status.includes('vencida') || (typeof dias === 'number' && dias < 0)) return 'Vencida';
    if (typeof dias === 'number' && dias <= 7) return 'Vencendo';
    return 'Ativa';
  }

  getStatusClass(item: AssinaturaDto): string {
    const status = this.normalizeStatus(item);
    if (status === 'Ativa') return 'bg-green-100 text-green-800';
    if (status === 'Vencendo') return 'bg-amber-100 text-amber-800';
    if (status === 'Vencida') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  }

  openFornecedor(item: AssinaturaDto) {
    if (!item?.fornecedor?.id) return;
    this.router.navigate(['/admin/fornecedores', item.fornecedor.id]);
  }

  openEditFornecedor(item: AssinaturaDto) {
    if (!item?.fornecedor?.id) return;
    this.router.navigate(['/admin/fornecedores', item.fornecedor.id, 'edit']);
  }

  goToRegistrarPagamento(item?: AssinaturaDto) {
    if (item?.fornecedor?.id) {
      this.router.navigate(['/admin/financeiro/registrar-pagamento'], { queryParams: { fornecedorId: item.fornecedor.id } });
      return;
    }
    this.router.navigate(['/admin/financeiro/registrar-pagamento']);
  }

  displayEnd(): number {
    const end = this.page * this.pageSize;
    const currentTotal = this.filteredItems.length;
    return end > currentTotal ? currentTotal : end;
  }
}
